import React from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  ShieldCheck,
  Award,
  Zap,
  TrendingUp,
  Headphones,
  CheckCircle2,
  HeartHandshake,
  Star,
  Check,
  MessageCircle,
  Clock,
  ThumbsUp,
  Users,
  Compass,
  MapPin,
} from 'lucide-react';

interface WhyChooseUsGridProps {
  badge?: string;
  title?: string;
  subtitle?: string;
}

// Icon helper map
const ICON_MAP: Record<string, any> = {
  ShieldCheck,
  Award,
  Zap,
  Sparkles: Star,
  TrendingUp,
  Headphones,
  CheckCircle2,
  HeartHandshake,
  Star,
  Clock,
  ThumbsUp,
  Users,
  Compass,
  MapPin,
};

export default function WhyChooseUsGrid({ badge, title, subtitle }: WhyChooseUsGridProps) {
  const { settings } = useApp();

  const config = settings?.whyChooseUs;
  const primaryColor = settings?.theme?.primaryColor || '#059669';
  const secondaryColor = settings?.theme?.secondaryColor || '#D97706';

  const sectionTitle = title || config?.title || 'Mengapa Memilih ALY LAW FIRM';
  const sectionSubtitle =
    subtitle ||
    config?.subtitle ||
    'Kantor Advokat & Konsultan Hukum Terpercaya dengan Integritas Tinggi, Pengalaman Litigasi & Non-Litigasi, serta Solusi Legal Komprehensif.';
  const sectionBadge = badge || config?.badge || 'INTEGRITAS & KEADILAN • ALY LAW FIRM';
  const seoIntro =
    config?.seoKeywordsIntro ||
    'Sebagai kantor advokat dan konsultan hukum independen, ALY LAW FIRM berdedikasi memberikan pendampingan hukum terbaik dalam perkara perdata, pidana, tipikor, litigasi, kontrak bisnis, dan hukum keluarga di seluruh Indonesia.';

  const features =
    config?.features && config.features.length > 0
      ? config.features
      : [
          {
            id: 'feat-1',
            icon: 'ShieldCheck',
            title: 'Pengalaman Litigasi & Penanganan Perkara',
            description:
              'Didampingi langsung oleh tim advokat senior berlisensi resmi PERADI dengan rekam jejak panjang di berbagai tingkatan peradilan dan instansi hukum.',
            badge: 'Berlisensi Resmi',
          },
          {
            id: 'feat-2',
            icon: 'Award',
            title: 'Integritas & Pembelaan Hak Klien',
            description:
              'Menjunjung tinggi prinsip etika profesi advokat, transparansi hukum, serta pembelaan hak-hak hukum klien secara maksimal.',
            badge: 'Integritas Tinggi',
          },
          {
            id: 'feat-3',
            icon: 'HeartHandshake',
            title: 'Solusi Legal Komprehensif',
            description:
              'Menyediakan layanan advokasi hukum lengkap mulai dari pendapat hukum (legal opinion), kontrak bisnis, hingga pendampingan pidana & perdata.',
            badge: 'Solusi Lengkap',
          },
          {
            id: 'feat-4',
            icon: 'Clock',
            title: 'Respon Cepat & Layanan Konsultasi',
            description:
              'Tim advokat kami siap memberikan konsultasi awal dan penanganan tindakan hukum dengan cepat, responsif, dan rahasia terjamin.',
            badge: 'Respon Cepat',
          },
        ];

  const richSeoContent =
    config?.richSeoContent ||
    'ALY LAW FIRM berkomitmen memberikan perlindungan dan kepastian hukum bagi perorangan maupun korporasi. Dengan pendekatan hukum yang tegas, terukur, dan profesional, kami siap menjadi mitra terpercaya dalam menyelesaikan setiap permasalahan hukum Anda.';

  const ctaTitle = config?.ctaTitle || 'Butuh Pendampingan atau Nasihat Hukum dari Advokat Profesional?';
  const ctaWaMessage = config?.ctaWaMessage || 'Halo Tim Advokat ALY LAW FIRM, saya ingin berkonsultasi mengenai permasalahan hukum.';

  const waNumber = settings?.contact?.whatsappNumber || '6282130955059';
  const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(ctaWaMessage)}`;

  return (
    <section
      id="why-choose-us-section"
      className="py-12 sm:py-16 bg-gradient-to-b from-slate-50 to-white border-y border-slate-200/80"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        {/* Header with Semantic H2 & Subtitle */}
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <span
            className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-[11px] font-extrabold tracking-wider uppercase text-white shadow-xs"
            style={{ backgroundColor: primaryColor }}
          >
            <Star className="w-3.5 h-3.5 fill-amber-300 text-amber-300" />
            <span>{sectionBadge}</span>
          </span>

          <h2
            id="why-choose-us-title"
            className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-snug"
          >
            {sectionTitle}
          </h2>

          <p className="text-sm sm:text-base text-slate-600 leading-relaxed font-normal">
            {sectionSubtitle}
          </p>
        </div>

        {/* SEO Keyword-Rich Paragraph Box */}
        {seoIntro && (
          <div className="p-5 sm:p-6 rounded-2xl bg-white border border-slate-200 shadow-xs max-w-5xl mx-auto">
            <div className="flex items-start gap-3.5">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-white font-bold shadow-xs"
                style={{ backgroundColor: primaryColor }}
              >
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <p className="text-xs sm:text-sm text-slate-700 leading-relaxed font-medium">
                {seoIntro}
              </p>
            </div>
          </div>
        )}

        {/* 6 Grid Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feat, idx) => {
            const IconComp = ICON_MAP[feat.icon] || ShieldCheck;
            return (
              <div
                key={feat.id || idx}
                className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-emerald-500/50 hover:shadow-lg transition-all duration-300 flex flex-col justify-between group space-y-4 relative overflow-hidden"
              >
                <div className="space-y-3">
                  {/* Top Row: Icon & Badge */}
                  <div className="flex items-center justify-between gap-2">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform duration-300"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <IconComp className="w-6 h-6" />
                    </div>

                    {feat.badge && (
                      <span
                        className="text-[10px] font-extrabold px-2.5 py-1 rounded-full uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200"
                      >
                        {feat.badge}
                      </span>
                    )}
                  </div>

                  {/* Title & Description with Semantic H3 */}
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 group-hover:text-emerald-800 transition-colors">
                    {feat.title}
                  </h3>

                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    {feat.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Rich SEO Content Footer & CTA Banner */}
        <div className="p-6 sm:p-8 lg:p-10 rounded-3xl bg-slate-900 text-white shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-72 h-72 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6 lg:gap-10">
            <div className="space-y-2.5 max-w-3xl lg:pr-4">
              <span className="text-[11px] font-extrabold text-amber-400 uppercase tracking-widest flex items-center gap-1.5">
                <Award className="w-4 h-4 text-amber-400" />
                <span>Guaranteed Quality & Academic Rigor</span>
              </span>
              <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-white tracking-tight leading-snug">
                {ctaTitle}
              </h4>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-normal">
                {richSeoContent}
              </p>
            </div>

            <div className="shrink-0 flex items-center">
              <a
                id="cta-whatsapp-consult-button"
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-7 py-4 rounded-xl font-bold text-xs sm:text-sm text-white flex items-center justify-center gap-2.5 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer text-center"
                style={{ backgroundColor: primaryColor }}
              >
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 fill-white text-white shrink-0" />
                <span className="whitespace-nowrap">{config?.ctaText || 'Contact Sustainability Advisory Now'}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
